import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Check } from 'lucide-react';
import { products } from '../data/mock';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Product Not Found</h1>
          <Link to="/" className="text-slate-600 hover:text-slate-900">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to={`/category/${product.category}`}
            className="inline-flex items-center text-slate-600 hover:text-slate-900 font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Category
          </Link>
        </div>
      </div>

      {/* Product Header */}
      <section className="relative py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="flex justify-center lg:justify-start relative z-10">
              <div className="relative w-full max-w-md lg:max-w-xl aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-500 hover:scale-105">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-center"
                />
                {/* Optional glow overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10 pointer-events-none rounded-3xl"></div>
              </div>
            </div>

            {/* Product Info */}
            <div className="relative z-20">
              <h1 className="text-5xl font-bold text-slate-900 mb-4">{product.name}</h1>
              <p className="text-2xl text-slate-600 mb-8">{product.shortDescription}</p>
              <p className="text-lg text-slate-700 leading-relaxed mb-8">{product.fullDescription}</p>

              {/* Package Information */}
              {product.packages && (
                <div className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">Available Packages:</h3>
                  <p className="text-slate-700">{product.packages}</p>
                </div>
              )}

              {/* Sustainability Badge */}
              {product.sustainability && (
                <div className="mb-8 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-start">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-green-800 font-medium">{product.sustainability}</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/request-quote"
                  className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg"
                >
                  Request Quote
                </Link>
                <a
                  href={product.technicalSheet}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold text-lg"
                >
                  <Download className="mr-2 w-5 h-5" />
                  Download Technical Datasheet
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 transition-colors font-semibold text-lg"
                >
                  Request Information
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Optional background accent behind image */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-slate-50 via-transparent to-transparent z-0 pointer-events-none rounded-l-3xl"></div>
      </section>

      {/* Product Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Key Properties */}
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Properties</h2>
              <ul className="space-y-3">
                {product.properties.map((prop, index) => (
                  <li key={index} className="flex items-start text-slate-700">
                    <span className="inline-block w-2 h-2 bg-slate-900 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>{prop}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Advantages */}
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Advantages</h2>
              <ul className="space-y-3">
                {product.advantages.map((adv, index) => (
                  <li key={index} className="flex items-start text-slate-700">
                    <Check className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                    <span>{adv}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Applications */}
            <div className="bg-white rounded-xl p-8 shadow-md">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Typical Applications</h2>
              <ul className="space-y-3">
                {product.applications.map((app, index) => (
                  <li key={index} className="flex items-start text-slate-700">
                    <span className="inline-block w-2 h-2 bg-slate-900 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>{app}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Need More Information?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Contact us for detailed specifications, application guidance, and product consultation.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center px-8 py-4 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors font-semibold text-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;